import { render as renderElement } from "preact";
import { createBrowserHistory } from "history";
import { getGlobal } from "@nevoland/get-global";
import {
  ContextPortal,
  ProviderHistory,
  ProviderTooltip,
} from "@airvantage/ui";
import { unescape } from "lodash-es";
import * as ICON_MAP from "@airvantage/ui/icons.js";
import { ContextEvent } from "@airvantage/ui";
import createCache from "@emotion/cache";
import { CacheProvider } from "@emotion/react";
import { StylesProvider, jssPreset } from "@mui/styles";
import { create } from "jss";
import { createTheme, CssBaseline } from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import { EMPTY_ARRAY } from "unchangeable";

import { ComponentLoader } from "./ComponentLoader.jsx";
import { ProviderGlobal } from "./providers/ProviderGlobal.jsx";
import { ProviderViewList } from "./providers/ProviderViewList.jsx";
import { StyleSheetLoader } from "./components/StyleSheetLoader.js";

const BASE = "/v5";

function linkStyleSheet(node: ParentNode, path: string) {
  const style = document.createElement("link");
  style.setAttribute("rel", "stylesheet");
  style.setAttribute("href", path);
  const previousStyle = node.children[node.children.length - 2];
  if (!previousStyle) {
    node.appendChild(style);
    return style;
  }
  style.addEventListener("load", () => {
    const oldStyle = node.firstElementChild;
    if (!oldStyle) {
      return style;
    }
    node.removeChild(oldStyle);
  });
  node.insertBefore(style, node.lastElementChild);
  return style;
}

function getBaseURL() {
  return process.env.NODE_ENV === "production"
    ? `${getGlobal().resourcesUrlBase ?? ""}${BASE}`
    : BASE;
}

class ReactPortal extends HTMLElement {
  mounted = false;
  connectedCallback() {
    if (this.mounted) {
      return;
    }
    this.mounted = true;
    const baseUrl = getBaseURL();
    const shadow = this.attachShadow({ mode: "open" });
    linkStyleSheet(shadow, `${baseUrl}/main.css`);
    if (import.meta.hot) {
      import.meta.hot.on("vite:afterUpdate", (event) => {
        if (event?.type !== "update") {
          return;
        }
        if (!event.updates?.length) {
          return;
        }
        for (const update of event.updates) {
          if (update.type === "css-update") {
            linkStyleSheet(shadow, `${baseUrl}${update.path}`);
          }
        }
      });
    }
    const mountPoint = document.createElement("div");
    mountPoint.id = "Host";
    mountPoint.classList.add("Root");
    shadow.appendChild(mountPoint);
    renderElement(null, mountPoint);
  }
}

// Unfortunately, there is no `undefine` to support HMR: https://github.com/WICG/webcomponents/issues/754
customElements.define("react-portal", ReactPortal);

function getPortalElement(): HTMLDivElement {
  const currentPortal = document.getElementById("Portal");
  if (currentPortal !== null) {
    return currentPortal.shadowRoot!.children[1] as HTMLDivElement;
  }
  const portal = document.createElement("react-portal");
  portal.setAttribute("id", "Portal");
  portal.style.zIndex = "10002";
  portal.style.position = "absolute";
  document.body.appendChild(portal);
  return portal.shadowRoot!.children[1] as HTMLDivElement;
}

function userContext(props: Record<string, any>) {
  const user = props.user ? JSON.parse(props.user) : undefined;
  return { userRights: user?.rights };
}

function render(
  componentName: string | undefined,
  props: Record<string, any>,
  mountPoint: HTMLDivElement,
  styleSheetList?: readonly HTMLLinkElement[]
) {
  renderElement(
    <StyleSheetLoader value={styleSheetList}>
      <ProviderHistory
        value={Object.assign(createBrowserHistory(), { browser: true })}
      >
        <ContextPortal.Provider value={getPortalElement()}>
          <ContextEvent.Provider value={getGlobal().ebus}>
            <ProviderGlobal>
              <ProviderViewList context={userContext(props)}>
                <ProviderTooltip>
                  <ComponentLoader
                    componentName={componentName}
                    props={props}
                  />
                </ProviderTooltip>
              </ProviderViewList>
            </ProviderGlobal>
          </ContextEvent.Provider>
        </ContextPortal.Provider>
      </ProviderHistory>
    </StyleSheetLoader>,
    mountPoint
  );
}

function renderStandalone(
  componentName: string | undefined,
  props: Record<string, any>,
  mountPoint: HTMLDivElement
) {
  render(componentName, props, mountPoint, [
    linkStyleSheet(
      getGlobal()!.document!.head,
      `${getBaseURL()}/standalone.css`
    ),
  ]);
}

getGlobal().render = renderStandalone;

class ReactComponent extends HTMLElement {
  mountPoint = document.createElement("div");
  mounted = false;
  styleSheetList: readonly HTMLLinkElement[] = EMPTY_ARRAY;

  constructor() {
    super();
    this.mountPoint.id = "Host";
    this.mountPoint.classList.add("Root");
  }

  static get observedAttributes() {
    return ["value"];
  }

  attributeChangedCallback() {
    this.render();
  }

  connectedCallback() {
    if (this.mounted) {
      return;
    }
    this.mounted = true;
    const baseUrl = getBaseURL();
    const shadow = this.attachShadow({ mode: "open" });
    this.styleSheetList = [linkStyleSheet(shadow, `${baseUrl}/main.css`)];

    if (import.meta.hot) {
      import.meta.hot.on("vite:afterUpdate", (event) => {
        if (event?.type !== "update") {
          return;
        }
        if (!event.updates?.length) {
          return;
        }
        for (const update of event.updates) {
          if (update.type === "css-update") {
            linkStyleSheet(shadow, `${baseUrl}${update.path}`);
          }
        }
      });
    }

    const mode = this.getAttribute("mode") ?? undefined;
    if (mode === "inline") {
      this.mountPoint.classList.add("Host-inline");
    } else if (mode === "modal") {
      this.mountPoint.classList.add("Host-modal");
    }

    // TODO: Encupsalte the dark mode toggle in a function
    // const darkMode = getGlobal().matchMedia("(prefers-color-scheme: dark)");
    // const setDarkMode = (dark: boolean) => {
    //   if (dark) {
    //     this.mountPoint.classList.add("dark");
    //     return;
    //   }
    //   this.mountPoint.classList.remove("dark");
    // };
    // darkMode.addEventListener("change", (event) => setDarkMode(event.matches));
    // setDarkMode(darkMode.matches);
    shadow.appendChild(this.mountPoint);

    this.render();
  }

  render() {
    render(
      this.getAttribute("component") ?? undefined,
      this.getProps(),
      this.mountPoint,
      this.styleSheetList
    );
  }

  getProps() {
    const attributeNameList = this.getAttributeNames();
    const props: Record<string, any> = {};
    for (const name of attributeNameList) {
      switch (name) {
        case "component":
        case "mode":
          continue;
        default:
          props[name] = this.getAttribute(name);
      }
      const value = this.getAttribute(name);
      props[name] = value == null ? null : unescape(value);
    }

    return props;
  }
}

// Unfortunately, there is no `undefine` to support HMR: https://github.com/WICG/webcomponents/issues/754
customElements.define("react-component", ReactComponent);

// TODO: Move to separate module
class ReactAccountComponent extends HTMLElement {
  connectedCallback() {
    const baseUrl = getBaseURL();
    // If closed, impossible to call `querySelector` on the shadowRoot later
    // Might be linked to the fact that the component gets detached and re-attached
    const shadow = this.attachShadow({ mode: "open" });
    linkStyleSheet(shadow, `${baseUrl}/main.css`);
    linkStyleSheet(shadow, `${baseUrl}/account.css`);

    if (import.meta.hot) {
      import.meta.hot.on("vite:afterUpdate", (event) => {
        if (event?.type !== "update") {
          return;
        }
        if (!event.updates?.length) {
          return;
        }
        for (const update of event.updates) {
          if (update.type === "css-update") {
            linkStyleSheet(shadow, `${baseUrl}${update.path}`);
          }
        }
      });
    }
    const mountPoint = document.createElement("div");
    mountPoint.id = "Host";
    mountPoint.classList.add("Root");

    shadow.appendChild(mountPoint);

    const cashCache = createCache({
      key: "css",
      prepend: true,
      container: mountPoint,
      speedy: false,
    });

    const jss = create({
      ...jssPreset(),
      insertionPoint: mountPoint,
    });

    const shadowTheme = createTheme({
      typography: {
        fontFamily: [
          "Roboto",
          "Oxygen",
          "Ubuntu",
          "Cantarell",
          "Fira Sans",
          "Droid Sans",
          "Helvetica Neue",
          "sans-serif",
        ].join(","),
      },
      components: {
        MuiPopover: {
          defaultProps: {
            container: mountPoint,
          },
        },
        MuiPopper: {
          defaultProps: {
            container: mountPoint,
          },
          styleOverrides: {
            root: {
              zIndex: "10003 !important",
            },
          },
        },
        MuiModal: {
          defaultProps: {
            container: mountPoint,
          },
          styleOverrides: {
            root: {
              zIndex: 10002,
            },
          },
        },
        MuiTextField: {
          defaultProps: {
            variant: "standard",
          },
        },
        MuiInputLabel: {
          defaultProps: {
            shrink: true,
          },
          styleOverrides: {
            root: {
              fontSize: "1.23rem",
            },
          },
        },
        MuiButtonBase: {
          styleOverrides: {
            root: {
              fontSize: "1rem",
            },
          },
          defaultProps: {
            disableRipple: true,
          },
        },
        MuiButton: {
          styleOverrides: {
            root: {
              fontSize: "1rem",
            },
          },
        },
        MuiLink: {
          defaultProps: {
            underline: "none",
          },
          styleOverrides: {
            root: {
              fontSize: "1.23rem",
            },
          },
        },
        MuiPaper: {
          styleOverrides: {
            root: {
              color: "inherit",
              fontSize: "1.23rem",
            },
          },
        },
        MuiChip: {
          styleOverrides: {
            root: {
              fontSize: "1rem",
            },
          },
        },
        MuiTypography: {
          styleOverrides: {
            root: {
              fontSize: "1.23rem",
            },
          },
        },
        MuiInput: {
          styleOverrides: {
            root: {
              fontSize: "1.23rem",
            },
            underline: {
              "&:before": {
                borderBottom: "1px solid rgba(0, 0, 0, 0.08)",
              },
              "&:hover:not(.Mui-disabled):before": {
                borderBottom: "1px solid rgba(0, 0, 0, 0.16)",
              },
              "&:after": {
                borderBottom: "1px solid rgba(0, 0, 0, 0.8)",
              },
            },
          },
        },
        MuiFormLabel: {
          styleOverrides: {
            root: {
              "&.Mui-focused": {
                // increase the specificity for the pseudo class
                color: "rgba(0, 0, 0, 0.8)",
              },
            },
          },
        },
      },
    });

    const componentName = this.getAttribute("component") ?? undefined;
    const attributeNameList = this.getAttributeNames();
    const props: Record<string, any> = {};
    for (const name of attributeNameList) {
      if (name === "component") {
        continue;
      }
      props[name] = this.getAttribute(name);
    }
    renderElement(
      <ProviderHistory value={createBrowserHistory()}>
        <ContextPortal.Provider value={mountPoint}>
          <CacheProvider value={cashCache}>
            <StylesProvider jss={jss}>
              <ThemeProvider theme={shadowTheme}>
                <CssBaseline />
                <ProviderGlobal>
                  <ProviderViewList context={userContext(props)}>
                    <ProviderTooltip>
                      <ComponentLoader
                        componentName={componentName}
                        props={props}
                      />
                    </ProviderTooltip>
                  </ProviderViewList>
                </ProviderGlobal>
              </ThemeProvider>
            </StylesProvider>
          </CacheProvider>
        </ContextPortal.Provider>
      </ProviderHistory>,
      mountPoint
    );
  }
}

// Unfortunately, there is no `undefine` to support HMR: https://github.com/WICG/webcomponents/issues/754
customElements.define("react-account-component", ReactAccountComponent);

// TODO: Move to separate module
class ReactSignUpComponent extends HTMLElement {
  connectedCallback() {
    const baseUrl = getBaseURL();
    // If closed, impossible to call `querySelector` on the shadowRoot later
    const shadow = this.attachShadow({ mode: "open" });
    linkStyleSheet(shadow, `${baseUrl}/main.css`);
    linkStyleSheet(shadow, `${baseUrl}/signup.css`);

    if (import.meta.hot) {
      import.meta.hot.on("vite:afterUpdate", (event) => {
        if (event?.type !== "update") {
          return;
        }
        if (!event.updates?.length) {
          return;
        }
        for (const update of event.updates) {
          if (update.type === "css-update") {
            linkStyleSheet(shadow, `${baseUrl}${update.path}`);
          }
        }
      });
    }
    const mountPoint = document.createElement("div");
    mountPoint.id = "Host";
    mountPoint.classList.add("Root");

    shadow.appendChild(mountPoint);

    const cashCache = createCache({
      key: "css",
      prepend: true,
      container: mountPoint,
      speedy: false,
    });

    const jss = create({
      ...jssPreset(),
      insertionPoint: mountPoint,
    });

    const shadowTheme = createTheme({
      typography: {
        fontFamily: [
          "Roboto",
          "Oxygen",
          "Ubuntu",
          "Cantarell",
          "Fira Sans",
          "Droid Sans",
          "Helvetica Neue",
          "sans-serif",
        ].join(","),
      },
      components: {
        MuiPopover: {
          defaultProps: {
            container: mountPoint,
          },
        },
        MuiPopper: {
          defaultProps: {
            container: mountPoint,
          },
        },
        MuiModal: {
          defaultProps: {
            container: mountPoint,
          },
        },
        MuiTextField: {
          defaultProps: {
            variant: "standard",
          },
        },
        MuiInputLabel: {
          defaultProps: {
            shrink: true,
          },
        },
        MuiButtonBase: {
          defaultProps: {
            disableRipple: true,
          },
        },
        MuiLink: {
          defaultProps: {
            underline: "none",
          },
        },
        MuiPaper: {
          styleOverrides: {
            root: {
              color: "inherit",
            },
          },
        },
        MuiFormLabel: {
          styleOverrides: {
            root: {
              "&.Mui-focused": {
                // increase the specificity for the pseudo class
                color: "rgba(0, 0, 0, 0.8)",
              },
            },
          },
        },
      },
    });

    const componentName = this.getAttribute("component") ?? undefined;
    const attributeNameList = this.getAttributeNames();
    const props: Record<string, any> = {};
    for (const name of attributeNameList) {
      if (name === "component") {
        continue;
      }
      props[name] = this.getAttribute(name);
    }
    renderElement(
      <ProviderHistory value={createBrowserHistory()}>
        <ContextPortal.Provider value={mountPoint}>
          <CacheProvider value={cashCache}>
            <StylesProvider jss={jss}>
              <ThemeProvider theme={shadowTheme}>
                <CssBaseline />
                <ProviderGlobal>
                  <ProviderViewList context={userContext(props)}>
                    <ProviderTooltip>
                      <ComponentLoader
                        componentName={componentName}
                        props={props}
                      />
                    </ProviderTooltip>
                  </ProviderViewList>
                </ProviderGlobal>
              </ThemeProvider>
            </StylesProvider>
          </CacheProvider>
        </ContextPortal.Provider>
      </ProviderHistory>,
      mountPoint
    );
  }
}

// Unfortunately, there is no `undefine` to support HMR: https://github.com/WICG/webcomponents/issues/754
customElements.define("react-signup-component", ReactSignUpComponent);
class ReactIcon extends HTMLElement {
  mountPoint = document.createElement("span");
  mounted = false;
  constructor() {
    super();
  }
  connectedCallback() {
    if (this.mounted) {
      return;
    }
    this.mounted = true;
    this.appendChild(this.mountPoint);
    this.mountPoint.id = "icon";
    const iconName = this.getAttribute("iconName") ?? "";
    const Component = ICON_MAP[iconName as keyof typeof ICON_MAP] || null;
    if (Component) {
      renderElement(<Component />, this.mountPoint);
    }
  }
}

customElements.define("react-icon", ReactIcon);
